/**
 * Campaigns Page
 *
 * Cross-link UTM campaign performance dashboard that aggregates analytics
 * across all links sharing the same utm_campaign value.
 *
 * @module pages/campaigns
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useQueryState, parseAsInteger } from 'nuqs';
import {
  Megaphone,
  TrendingUp,
  Link2,
  MousePointerClick,
  ChevronRight,
  Calendar,
  Filter,
  BarChart3,
} from 'lucide-react';
import { cn, formatNumber } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Skeleton } from '@/client/components/ui/skeleton';

// Type definitions
interface TopLink {
  shortCode: string;
  title: string | null;
  clicks: number;
}

interface Campaign {
  utmCampaign: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  totalClicks: number;
  uniqueLinks: number;
  topLinks: TopLink[];
}

interface CampaignsResponse {
  campaigns: Campaign[];
  summary: {
    totalCampaigns: number;
    totalClicks: number;
    topSource: string | null;
    topMedium: string | null;
  };
  dateRange: {
    start: string;
    end: string;
    days: number;
  };
}

const DATE_RANGES = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
] as const;

function getSourceIcon(source: string | null): string {
  const s = source?.toLowerCase();
  if (s?.includes('facebook') || s?.includes('fb')) return 'bg-blue-600';
  if (s?.includes('twitter') || s?.includes('x.com')) return 'bg-sky-500';
  if (s?.includes('instagram')) return 'bg-pink-500';
  if (s?.includes('linkedin')) return 'bg-blue-700';
  if (s?.includes('google')) return 'bg-red-500';
  if (s?.includes('email') || s?.includes('newsletter')) return 'bg-primary';
  if (s?.includes('tiktok')) return 'bg-zinc-900';
  return 'bg-muted-foreground';
}

export function CampaignsPage() {
  const [days, setDays] = useQueryState('days', parseAsInteger.withDefault(30));

  const { data, isLoading, error } = useQuery<CampaignsResponse>({
    queryKey: ['campaigns', days],
    queryFn: async () => {
      const response = await apiFetch(`/api/campaigns?days=${days}`);
      if (!response.ok) throw new Error('Failed to fetch campaigns');
      return response.json();
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-40 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-48" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-2xl" />
          ))}
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <p className="text-muted-foreground mb-4">Failed to load campaigns</p>
        <Button onClick={() => window.location.reload()} className="rounded-xl">
          Retry
        </Button>
      </div>
    );
  }

  // Data is guaranteed to exist after loading check above
  const campaigns = data?.campaigns ?? [];
  const summary = data?.summary ?? {
    totalCampaigns: 0,
    totalClicks: 0,
    topSource: null,
    topMedium: null,
  };

  return (
    <div className="space-y-6 stagger-children">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Campaigns</h1>
          <p className="text-muted-foreground mt-1">
            Track UTM campaign performance across all your links
          </p>
        </div>

        {/* Date Range Selector */}
        <div className="flex items-center gap-2">
          <Calendar className="size-4 text-muted-foreground" />
          <div className="flex rounded-xl border bg-card overflow-hidden">
            {DATE_RANGES.map((range) => (
              <Button
                key={range.value}
                variant={days === range.value ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setDays(range.value)}
                className={cn('rounded-none', days === range.value && 'rounded-xl')}
              >
                {range.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-2xl py-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Megaphone className="size-4" />
              Active Campaigns
            </div>
            <p className="text-2xl font-bold">{summary.totalCampaigns}</p>
          </CardContent>
        </Card>

        <Card className="rounded-2xl py-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <MousePointerClick className="size-4" />
              Total Clicks
            </div>
            <p className="text-2xl font-bold tabular-nums">{formatNumber(summary.totalClicks)}</p>
          </CardContent>
        </Card>

        <Card className="rounded-2xl py-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <TrendingUp className="size-4" />
              Top Source
            </div>
            <p className="text-2xl font-bold capitalize">{summary.topSource || '—'}</p>
          </CardContent>
        </Card>

        <Card className="rounded-2xl py-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Filter className="size-4" />
              Top Medium
            </div>
            <p className="text-2xl font-bold capitalize">{summary.topMedium || '—'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <Card className="rounded-2xl py-0">
          <CardContent className="p-12 text-center">
            <div className="size-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="size-8 text-muted-foreground/50" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No campaigns yet</h3>
            <p className="text-muted-foreground mb-4">
              Start tracking campaigns by adding UTM parameters to your links.
            </p>
            <Button asChild className="rounded-xl">
              <Link to="/links">
                <Link2 className="size-4" />
                Go to Links
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <Card
              key={campaign.utmCampaign}
              className="rounded-2xl hover:border-primary/50 transition-colors py-0"
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    {/* Campaign Icon */}
                    <div
                      className={cn(
                        'size-10 rounded-xl flex items-center justify-center',
                        getSourceIcon(campaign.utmSource)
                      )}
                    >
                      <Megaphone className="size-5 text-white" />
                    </div>

                    <div>
                      <h3 className="font-semibold text-lg">
                        {campaign.utmCampaign || 'Unnamed Campaign'}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1 flex-wrap">
                        {campaign.utmSource && (
                          <Badge variant="secondary" className="capitalize">
                            {campaign.utmSource}
                          </Badge>
                        )}
                        {campaign.utmMedium && (
                          <Badge variant="outline" className="capitalize">
                            {campaign.utmMedium}
                          </Badge>
                        )}
                        <span className="text-muted-foreground/70">
                          {campaign.uniqueLinks} {campaign.uniqueLinks === 1 ? 'link' : 'links'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-2xl font-bold tabular-nums">
                      {formatNumber(campaign.totalClicks)}
                    </p>
                    <p className="text-sm text-muted-foreground">clicks</p>
                  </div>
                </div>

                {/* Top Links */}
                {campaign.topLinks.length > 0 && (
                  <div className="border-t pt-4">
                    <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                      Top Performing Links
                    </p>
                    <div className="space-y-1">
                      {campaign.topLinks.slice(0, 3).map((link, idx) => (
                        <Link
                          key={link.shortCode}
                          to={`/links/${link.shortCode}`}
                          className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/50 transition-colors group"
                        >
                          <div className="flex items-center gap-3">
                            <span className="size-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                              {idx + 1}
                            </span>
                            <span className="text-sm font-medium truncate max-w-[300px]">
                              {link.title || `arml.ink/${link.shortCode}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground group-hover:text-foreground">
                            <span className="tabular-nums">{formatNumber(link.clicks)}</span>
                            <ChevronRight className="size-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Help Text */}
      <Card className="rounded-2xl border-dashed py-0">
        <CardContent className="p-4">
          <h4 className="font-medium mb-2">How to track campaigns</h4>
          <p className="text-sm text-muted-foreground">
            Add UTM parameters when creating links to track campaign performance. Use consistent
            naming (e.g.,{' '}
            <code className="bg-muted px-1.5 py-0.5 rounded text-xs">utm_campaign=summer-sale</code>
            ) across all links in a campaign to see aggregated metrics here.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
